<template>
  <div class="d-profile-bookshelf-list-entry">
    <div class="cover">
      <a
        v-if="entry.book_type === 'ExternalBook'"
        :href="bookUrl"
        title="View book"
      >
        <DuiBookCover
          :book-cover-url="entry.book.covers.large"
        />
      </a>

      <a
        v-else
        :href="bookUrl"
        title="View book"
      >
        <DuiBookCover
          :book-cover-url="entry.book.cover.medium.url"
        />
      </a>
    </div>

    <div class="top-content">
      <div class="left">
        <h5>{{ entry.created_at }}</h5>
      </div>
      <div class="right">
        <a
          v-if="entry.book_type === 'ExternalBook'"
          class="label label-round label-xs"
          :href="entry.book.url"
          rel="nofollow"
          target="_blank"
          title="View book"
        >
          {{ entry.book.provider }}
        </a>

        <a
          v-else
          class="label label-round label-xs"
          :href="entry.book.url"
          title="View book"
        >
          Discovery
        </a>
      </div>
    </div>

    <div class="center-content">
      <div>
        <a
          class="book-title"
          :href="bookUrl"
          title="View book"
        >
          <span class="h3">{{ entry.book.title }}</span>
        </a>
      </div>

      <div class="book-info grid-flex grid-middle">
        <div class="grid-item-shrink">
          <span
            v-if="!!entry.book.author"
            class="author space-right-1em valign-middle"
          >{{ entry.book.author }}</span>

          <DuiReviewRating
            v-if="!!review"
            class="space-right-1em"
            :rating="review.verdict.rating"
            :video-review="!!review.video_url"
          />
        </div>

        <div class="grid-item">
          <span class="label label-sm label-sand">{{ getBookshelfText() }}</span>
        </div>
      </div>

      <div
        v-if="entry.book_type === 'Book'"
        class="space-top-2em"
      >
        {{ getBookSynopsis() }}
      </div>
    </div>
  </div>
</template>

<script>
import bookshelfService from '@d-services/bookshelf-service/bookshelf-service';
import duiPlainText from '@dui/filters/dui-plain-text/dui-plain-text';
import duiTruncate from '@dui/filters/dui-truncate/dui-truncate';
import URLS from '@d-shared/urls/urls';

export default {
  props: {
    entry: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    bookUrl: null,
    review: null,
  }),
  mounted() {
    this.initBookReview();
    this.initBookUrl();
  },
  methods: {
    getBookSynopsis() {
      if (!this.entry.book.synopsis) {
        return null;
      }

      return duiTruncate(duiPlainText(this.entry.book.synopsis), 100);
    },

    getBookshelfText() {
      return bookshelfService.getBookshelfOptionText(this.entry.bookshelf);
    },

    initBookReview() {
      if (!!this.entry.review) {
        this.$set(this, 'review', this.entry.review);
        return;
      }

      if (!!this.entry.user_review) {
        this.$set(this, 'review', this.entry.user_review);
      }
    },

    initBookUrl() {
      if (!!this.entry.review) {
        this.$set(this, 'bookUrl', this.entry.book.url);
        return;
      }

      if (!!this.entry.user_review && !!this.entry.user_review.overview) {
        this.$set(this, 'bookUrl', this.entry.user_review.url);
        return;
      }

      const bookUrl = (this.entry.book_type === 'ExternalBook')
        ? URLS.app.bookUserReviews.replace('<book-slug>', this.entry.book.slug)
        : this.entry.book.url;

      this.$set(this, 'bookUrl', bookUrl);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@dui-styles/base";

.d-profile-bookshelf-list-entry {
  display: grid;
  grid-column-gap: $_2em;
  grid-row-gap: $_2em;
  grid-template-areas:
    "cover top-content"
    "cover center-content";
  grid-template-columns: auto 1fr;
  grid-template-rows: auto 1fr;
  padding: $_2em 0;

  @include screenMoreThan(sm) {
    grid-template-columns: $book-cover-width-sm auto;
    padding: $_3em 0;
  }

  .cover {
    grid-area: cover;

    a {
      display: flex;
    }

    .dui-book-cover {
      height: $book-cover-height-xs;
      width: $book-cover-width-xs;

      &.square {
        height: $book-cover-width-xs;
      }

      @include screenMoreThan(sm) {
        height: $book-cover-height-sm;
        width: $book-cover-width-sm;

        &.square {
          height: $book-cover-width-sm;
        }
      }
    }
  }

  .top-content {
    align-items: center;
    display: flex;
    grid-area: top-content;

    .left {
      flex: 1;

      h5 {
        margin: 0;
      }
    }

    .right {
      flex: 0 1 auto;

      .label {
        font-size: $font-size-xs;
        text-transform: uppercase;
      }
    }
  }

  .center-content {
    grid-area: center-content;

    .book-title {
      color: getColor($black);
      display: flex;
    }

    .book-info {
      .author {
        display: inline-block;
      }

      @include screenLessThan(lg) {
        .grid-item,
        .grid-item-shrink {
          flex: 0 0 100%;
        }

        .author,
        .dui-review-rating {
          margin-bottom: $_1em;
        }
      }
    }
  }

  .bottom-content {
    grid-area: bottom-content;

    .dui-dropdown {
      margin-bottom: $_1em;
      width: 100%;

      @include screenMoreThan(xs) {
        margin: 0;
        width: 200px;
      }
    }
  }
}
</style>
